import { useEffect, useMemo, useRef, useState } from "react";
import { apiGet, apiSend } from "../api";
import ResultModal from "../components/ResultModal";

// 生成台:运营主战场。
// 顶部:统计卡(日/周/月/季/年 × 生成次数 + 照片数)—— 每次都花钱,要长期盯着。
// 主区:选模板 → 上传自拍 → 生成 → 出成品 + 二维码。
// 生成成功后不放"再来一次",要新生成必须回上一步重选,防止误点烧钱。

interface Template {
  id: string; name: string; category: string; subcategory: string; subsubcategory: string;
  image: string; enabled: boolean; usedCount: number;
}
type Subject = "male" | "female" | "boy" | "girl";
const SUBJECTS: { key: Subject; label: string }[] = [
  { key: "male", label: "男" },
  { key: "female", label: "女" },
  { key: "boy", label: "男娃" },
  { key: "girl", label: "女娃" },
];
const SUBJECT_CATEGORY: Record<Subject, string> = {
  male: "男",
  female: "女",
  boy: "男娃",
  girl: "女娃",
};
interface CategoryNode {
  id: string; name: string; enabled: boolean;
  children?: CategoryNode[];
}

const PERIODS: { key: string; label: string }[] = [
  { key: "day", label: "今日" },
  { key: "week", label: "本周" },
  { key: "month", label: "本月" },
  { key: "quarter", label: "本季度" },
  { key: "year", label: "本年" },
];

export default function Studio() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [tree, setTree] = useState<CategoryNode[]>([]);
  const [selectedTop, setSelectedTop] = useState<string | null>(null);
  const [selectedSub, setSelectedSub] = useState<string | null>(null);
  const [selectedSub2, setSelectedSub2] = useState<string | null>(null);
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [selfie, setSelfie] = useState<File | null>(null);
  const [selfieUrl, setSelfieUrl] = useState<string>("");
  const [subject, setSubject] = useState<Subject | null>(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ id: string; resultUrl: string; qrPayload: string; templateName?: string } | null>(null);
  const [statsToken, setStatsToken] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);

  const [actionBarHeight, setActionBarHeight] = useState(0);
  const actionBarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const bar = actionBarRef.current;
    if (!bar) {
      setActionBarHeight(0);
      return;
    }

    const updateHeight = () => {
      setActionBarHeight(bar.offsetHeight);
    };

    updateHeight();

    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(() => {
        updateHeight();
      });
      observer.observe(bar);
    }

    window.addEventListener("resize", updateHeight);

    return () => {
      if (observer) {
        observer.disconnect();
      }
      window.removeEventListener("resize", updateHeight);
    };
  }, [result]);

  const load = async () => {
    const [tpls, cats] = await Promise.all([
      apiGet<Template[]>("/templates"),
      apiGet<CategoryNode[]>("/categories"),
    ]);
    setTemplates(tpls.filter((t) => t.enabled));
    setTree(cats);
  };
  useEffect(() => { load().catch((e) => setError(String(e))); }, []);

  const filtered = useMemo(() => {
    return templates.filter((t) => {
      const matchTop = selectedTop === null || t.category === selectedTop;
      const matchSub = selectedSub === null || t.subcategory === selectedSub;
      const matchSub2 = selectedSub2 === null || (t.subsubcategory || "") === selectedSub2;
      return matchTop && matchSub && matchSub2;
    });
  }, [templates, selectedTop, selectedSub, selectedSub2]);

  const picked = templates.find((t) => t.id === pickedId) || null;
  const subjectCategory = subject ? SUBJECT_CATEGORY[subject] : null;
  const randomCandidates = useMemo(() => {
    if (!subjectCategory) return [];
    return templates.filter((t) => t.category === subjectCategory);
  }, [templates, subjectCategory]);
  const canGenerate = !!selfie && !!subject && (!!picked || randomCandidates.length > 0);

  const chooseSubject = (next: Subject | null) => {
    setSubject(next);
    setResult(null);
    setError(null);
    if (!next) return;
    setSelectedTop(SUBJECT_CATEGORY[next]);
    setSelectedSub(null);
    setSelectedSub2(null);
    setPickedId(null);
  };

  const onSelfieChosen = (f: File | null) => {
    setSelfie(f);
    if (selfieUrl) URL.revokeObjectURL(selfieUrl);
    setSelfieUrl(f ? URL.createObjectURL(f) : "");
  };

  const doGenerate = async () => {
    if (!selfie || !subject || (!picked && randomCandidates.length === 0)) return;
    setError(null);
    setGenerating(true);
    try {
      const fd = new FormData();
      if (picked) fd.append("templateId", picked.id);
      fd.append("subject", subject);
      fd.append("file", selfie);
      const res = await apiSend<{
        id: string; status: string; resultUrl?: string; qrPayload?: string; error?: string;
        templateId?: string; templateName?: string;
      }>("POST", "/generate", fd);
      if (res.status !== "done" || !res.resultUrl) {
        throw new Error(res.error || "生成失败");
      }
      setResult({
        id: res.id,
        resultUrl: res.resultUrl,
        qrPayload: res.qrPayload || res.id,
        templateName: res.templateName || picked?.name,
      });
      setStatsToken((t) => t + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setGenerating(false);
    }
  };

  const reset = () => {
    // 重新走完整流程:清模板、清自拍、清身份、清结果。让运营明确"这是重新一次(要钱)"。
    // 分类筛选状态不清,保留运营刚才在哪一层筛。
    setPickedId(null);
    onSelfieChosen(null);
    setSubject(null);
    setResult(null);
    setError(null);
  };

  return (
    <div className="space-y-6">
      <StatsBar token={statsToken} />

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr_320px] gap-4">
        {/* ─── Category filter ──────────────────────────
            桌面(lg+):左侧竖排树,支持三级递归展开;
            手机 / 平板:横向 pill 组(顶级/二级/三级各一排,选了才出下一级)。 */}
        <div className="order-3 lg:order-none lg:sticky lg:top-16 lg:h-fit">
          {/* Desktop tree */}
          <div className="hidden lg:block bg-white rounded-xl border border-slate-200 p-3 space-y-1">
            <FilterButton
              label="全部模板" count={templates.length}
              active={selectedTop === null}
              onClick={() => { setSelectedTop(null); setSelectedSub(null); setSelectedSub2(null); }}
            />
            {tree.map((top) => (
              <TreeBranch
                key={top.id} node={top} depth={1}
                templates={templates}
                path={{ top: null, sub: null, sub2: null }}
                selected={{ top: selectedTop, sub: selectedSub, sub2: selectedSub2 }}
                onSelect={(t, s, s2) => { setSelectedTop(t); setSelectedSub(s); setSelectedSub2(s2); }}
              />
            ))}
          </div>
          {/* Mobile / tablet pills */}
          <div className="lg:hidden space-y-2 bg-white rounded-xl border border-slate-200 p-3">
            <PillRow
              items={[{ name: "全部", key: null }, ...tree.map((t) => ({ name: t.name, key: t.name }))]}
              selected={selectedTop}
              onSelect={(k) => { setSelectedTop(k); setSelectedSub(null); setSelectedSub2(null); }}
            />
            {selectedTop && (() => {
              const t = tree.find((x) => x.name === selectedTop);
              const subs = t?.children || [];
              if (subs.length === 0) return null;
              return (
                <PillRow
                  items={[{ name: "全部子分类", key: null }, ...subs.map((s) => ({ name: s.name, key: s.name }))]}
                  selected={selectedSub}
                  onSelect={(k) => { setSelectedSub(k); setSelectedSub2(null); }}
                  small
                />
              );
            })()}
            {selectedTop && selectedSub && (() => {
              const t = tree.find((x) => x.name === selectedTop);
              const s = t?.children?.find((x) => x.name === selectedSub);
              const sub2s = s?.children || [];
              if (sub2s.length === 0) return null;
              return (
                <PillRow
                  items={[{ name: "全部三级", key: null }, ...sub2s.map((s2) => ({ name: s2.name, key: s2.name }))]}
                  selected={selectedSub2}
                  onSelect={(k) => setSelectedSub2(k)}
                  small
                />
              );
            })()}
          </div>
        </div>

        {/* Middle: templates grid */}
        <div className="order-4 lg:order-none space-y-3">
          <div className="text-sm text-slate-500">
            {selectedTop ? `${selectedTop}${selectedSub ? " · " + selectedSub : ""}${selectedSub2 ? " · " + selectedSub2 : ""}` : "全部模板"}
            <span className="ml-2 text-slate-400">共 {filtered.length} 张</span>
          </div>
          {filtered.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center text-sm text-slate-400">
              这里还没有模板,去「模板管理」上传吧
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {filtered.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setPickedId((current) => current === t.id ? null : t.id)}
                  aria-pressed={pickedId === t.id}
                  className={`bg-white rounded-lg border p-2 text-left transition ${
                    pickedId === t.id ? "border-teal-500 ring-2 ring-teal-200" : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="aspect-[4/5] bg-slate-100 rounded overflow-hidden">
                    <img src={t.image} alt={t.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="mt-2 text-xs text-slate-700 line-clamp-1">{t.name}</div>
                  <div className="text-[10px] text-slate-400 line-clamp-1">
                    {[t.category, t.subcategory, t.subsubcategory].filter(Boolean).join(" · ")}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right: selfie + generate */}
        <div className="contents lg:block">
          <div className="contents lg:flex lg:flex-col lg:gap-3 lg:bg-white lg:rounded-xl lg:border lg:border-slate-200 lg:p-4 lg:sticky lg:top-16 lg:h-fit">
            {/* Block 2: Selfie / Camera / Local File (Selfie-before-identity on desktop, order-2 on mobile) */}
            <div className="order-2 lg:order-1 bg-white rounded-xl border border-slate-200 p-4 lg:p-0 lg:border-none lg:rounded-none space-y-3">
              <div className="text-sm font-medium text-slate-800 lg:block hidden">游客自拍</div>
              <div className="flex lg:flex-col gap-3 items-center lg:items-stretch">
                {/* Selfie dropzone/preview */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => fileRef.current?.click()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      fileRef.current?.click();
                    }
                  }}
                  onDragOver={(e) => { e.preventDefault(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const f = e.dataTransfer.files?.[0];
                    if (f && f.type.startsWith("image/")) onSelfieChosen(f);
                  }}
                  aria-label="上传游客自拍"
                  className="w-24 lg:w-full aspect-[4/5] bg-slate-50 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center overflow-hidden cursor-pointer hover:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 flex-shrink-0"
                >
                  {selfieUrl ? (
                    <img src={selfieUrl} alt="selfie" className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-xs text-slate-400 text-center px-1 lg:px-3 leading-relaxed">
                      <span className="lg:inline hidden">点击选择本地照片<br /><span className="text-[10px]">或直接拖入 · 推荐正脸 · 光线充足</span></span>
                      <span className="lg:hidden text-[10px]">点击上传<br />自拍照</span>
                    </div>
                  )}
                </div>
                {/* Buttons */}
                <div className="flex-1 flex flex-col lg:flex-row gap-2 w-full">
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="w-full px-3 py-2 lg:py-1.5 text-xs bg-slate-100 text-slate-700 rounded hover:bg-slate-200 font-semibold lg:font-normal min-h-[44px] lg:min-h-0 flex items-center justify-center"
                  >
                    📁 选本地文件
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const el = document.createElement("input");
                      el.type = "file";
                      el.accept = "image/*";
                      el.setAttribute("capture", "user");
                      el.onchange = () => onSelfieChosen((el.files?.[0]) || null);
                      el.click();
                    }}
                    className="w-full px-3 py-2 lg:py-1.5 text-xs bg-slate-100 text-slate-700 rounded hover:bg-slate-200 font-semibold lg:font-normal min-h-[44px] lg:min-h-0 flex items-center justify-center"
                  >
                    📷 现场拍照
                  </button>
                </div>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => onSelfieChosen(e.target.files?.[0] || null)}
              />
            </div>

            {/* Block 1: Identity Selection (Identity-after-selfie on desktop, order-1 on mobile) */}
            <div className="order-1 lg:order-2 bg-white rounded-xl border border-slate-200 p-4 lg:p-0 lg:border-none lg:rounded-none">
              <div className="text-xs text-slate-500 mb-1.5">游客身份</div>
              <div className="grid grid-cols-4 gap-1.5">
                {SUBJECTS.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => chooseSubject(subject === s.key ? null : s.key)}
                    aria-pressed={subject === s.key}
                    className={`px-2 py-2 rounded-md text-sm font-medium border transition min-h-[44px] lg:min-h-0 flex items-center justify-center ${
                      subject === s.key
                        ? "bg-teal-600 text-white border-teal-600"
                        : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Block 3: Action / Generation Area (Fixed Action Bar on mobile, static on desktop) */}
            <div
              ref={actionBarRef}
              className={`order-5 lg:order-3 fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-slate-200 p-4 shadow-lg pb-[calc(1rem+env(safe-area-inset-bottom))] lg:static lg:bottom-auto lg:left-auto lg:right-auto lg:z-auto lg:bg-transparent lg:border-none lg:p-0 lg:shadow-none lg:pb-0 lg:space-y-3 ${
                result ? "hidden" : "flex flex-col gap-2 lg:gap-0"
              }`}
            >
              {picked ? (
                <div className="text-xs text-slate-500 flex items-center gap-2">
                  已选模板 <b className="text-slate-700">{picked.name}</b>
                </div>
              ) : subjectCategory ? (
                <div className="text-xs text-slate-500 leading-relaxed">
                  未手动选择模板时,将从 <b className="text-slate-700">{subjectCategory}</b> 分类随机抽取
                  <span className="text-slate-400 ml-1">({randomCandidates.length} 张可用)</span>
                </div>
              ) : null}

              {error && (
                <div
                  role="alert"
                  aria-live="assertive"
                  className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded p-2 max-h-24 overflow-y-auto"
                >
                  {error}
                </div>
              )}

              <button
                type="button"
                disabled={!canGenerate || generating}
                onClick={doGenerate}
                className="w-full px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-40 min-h-[44px] lg:min-h-0 flex items-center justify-center"
              >
                {generating ? "生成中(约 30 秒)…" : picked ? "生成换脸大片" : "随机生成换脸大片"}
              </button>

              {!generating && (!canGenerate || !picked) && (
                <p
                  role="status"
                  aria-live="polite"
                  className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded p-2 leading-relaxed"
                >
                  {[
                    !selfie && "请上传游客自拍",
                    !subject && "请选游客身份(男/女/男娃/女娃)",
                    subject && !picked && randomCandidates.length === 0 && `「${subjectCategory}」分类暂无可用模板`,
                    subject && !picked && randomCandidates.length > 0 && `未手动选模板:将从「${subjectCategory}」随机抽取`,
                  ].filter(Boolean).join(" · ")}
                </p>
              )}

              <p className="text-[11px] text-slate-400 leading-relaxed lg:block hidden">
                每次生成都会调用换脸接口并计费。生成后无「再来一次」按钮 —— 需要新生成请重选模板与自拍。
              </p>
              <p className="text-[10px] text-slate-400 leading-normal lg:hidden">
                每次生成都会调用换脸接口并计费。
              </p>
            </div>

          </div>
        </div>

        {/* Spacer to reserve height for mobile fixed bottom bar */}
        {!result && (
          <div
            style={{ height: `${actionBarHeight + 16}px` }}
            className="order-6 lg:hidden"
          />
        )}

      </div>

      {result && (
        <ResultModal
          result={result}
          templateName={picked?.name}
          onReset={reset}
        />
      )}
    </div>
  );
}

// ─── Filter helpers ────────────────────────────────────────

function FilterButton({ label, count, active, indent, small, onClick }: {
  label: string; count?: number; active: boolean;
  indent?: 0 | 1 | 2; small?: boolean; onClick: () => void;
}) {
  const pad = indent === 2 ? "pl-8" : indent === 1 ? "pl-6" : "";
  const size = small ? "text-xs py-1.5" : "text-sm py-2";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left px-3 ${pad} ${size} rounded-lg ${
        active ? (small ? "bg-teal-100 text-teal-800" : "bg-teal-50 text-teal-700 font-medium") : "text-slate-600 hover:bg-slate-50"
      }`}
    >
      {label}{count !== undefined && <span className="text-xs text-slate-400 ml-1">({count})</span>}
    </button>
  );
}

// Recursively renders one branch. Each level offset by indent.
function TreeBranch({ node, depth, templates, path, selected, onSelect }: {
  node: CategoryNode; depth: 1 | 2 | 3;
  templates: Template[];
  path: { top: string | null; sub: string | null; sub2: string | null };
  selected: { top: string | null; sub: string | null; sub2: string | null };
  onSelect: (top: string | null, sub: string | null, sub2: string | null) => void;
}) {
  // Establish where in the path this node sits.
  const myPath = depth === 1
    ? { top: node.name, sub: null as string | null, sub2: null as string | null }
    : depth === 2
    ? { top: path.top, sub: node.name, sub2: null as string | null }
    : { top: path.top, sub: path.sub, sub2: node.name };

  const isActive =
    selected.top === myPath.top && selected.sub === myPath.sub && selected.sub2 === myPath.sub2;

  // Auto-expand if this branch is on the selected path.
  const onSelectedTopPath = depth === 1 && selected.top === node.name;
  const onSelectedSubPath = depth === 2 && selected.top === path.top && selected.sub === node.name;
  const open = onSelectedTopPath || onSelectedSubPath;

  // Count templates matching this branch's path.
  const count = templates.filter((t) => {
    if (depth === 1) return t.category === node.name;
    if (depth === 2) return t.category === path.top && t.subcategory === node.name;
    return t.category === path.top && t.subcategory === path.sub && (t.subsubcategory || "") === node.name;
  }).length;

  return (
    <div>
      <FilterButton
        label={node.name}
        count={count}
        active={isActive}
        indent={depth === 3 ? 2 : depth === 2 ? 1 : 0}
        small={depth >= 2}
        onClick={() => {
          if (isActive) {
            // Clicking active de-selects at that level.
            if (depth === 1) onSelect(null, null, null);
            else if (depth === 2) onSelect(myPath.top, null, null);
            else onSelect(myPath.top, myPath.sub, null);
          } else {
            onSelect(myPath.top, myPath.sub, myPath.sub2);
          }
        }}
      />
      {open && (node.children || []).map((child) => (
        <TreeBranch key={child.id} node={child} depth={(depth + 1) as 2 | 3}
          templates={templates} path={myPath} selected={selected} onSelect={onSelect} />
      ))}
    </div>
  );
}

function PillRow({ items, selected, onSelect, small }: {
  items: { name: string; key: string | null }[];
  selected: string | null;
  onSelect: (k: string | null) => void;
  small?: boolean;
}) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
      {items.map((it) => {
        const active = selected === it.key;
        return (
          <button
            key={String(it.key)}
            type="button"
            onClick={() => onSelect(it.key)}
            aria-pressed={active}
            className={`whitespace-nowrap rounded-full border flex items-center justify-center min-h-[44px] ${
              small ? "text-xs px-3" : "text-sm px-3.5"
            } ${
              active
                ? "bg-teal-600 text-white border-teal-600"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
            }`}
          >
            {it.name}
          </button>
        );
      })}
    </div>
  );
}

function StatsBar({ token }: { token: number }) {
  const [data, setData] = useState<Record<string, { attempts: number; photos: number }>>({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const entries = await Promise.all(PERIODS.map(async (p) => {
        const r = await apiGet<{ attempts: number; photos: number }>(`/stats?period=${p.key}`);
        return [p.key, { attempts: r.attempts, photos: r.photos }] as const;
      }));
      if (!cancelled) {
        setData(Object.fromEntries(entries));
        setLoading(false);
      }
    })().catch(() => {
      if (!cancelled) {
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="text-sm font-semibold text-slate-700">生成用量</div>
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
            className="lg:hidden text-xs text-teal-600 hover:text-teal-700 font-semibold focus:outline-none focus:ring-2 focus:ring-teal-500 rounded px-1.5 py-0.5 border border-teal-200 bg-teal-50/50"
          >
            {expanded ? "折叠" : "展开"}
          </button>
        </div>
        <div className="text-xs text-slate-400">次数 = 批次(一次多张算一次) · 张数 = 成功产出照片</div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {PERIODS.map((p, index) => {
          const d = data[p.key] || { attempts: 0, photos: 0 };
          return (
            <div
              key={p.key}
              className={`rounded-lg border border-slate-100 bg-slate-50/60 p-3 ${
                index >= 2 && !expanded ? "hidden lg:block" : "block"
              }`}
            >
              <div className="text-xs text-slate-500">{p.label}</div>
              <div className="mt-1 flex items-baseline gap-2">
                <div className="text-2xl font-bold text-slate-800 tabular-nums">{loading ? "—" : d.attempts}</div>
                <div className="text-xs text-slate-400">次</div>
              </div>
              <div className="mt-0.5 flex items-baseline gap-2">
                <div className="text-lg font-semibold text-emerald-600 tabular-nums">{loading ? "—" : d.photos}</div>
                <div className="text-xs text-slate-400">张</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
